/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/

export interface Translation {
    de: string;
    en: string;
    fr: string;
}

export interface CountryData {
    id: string;
    currency: string;
    vat: number;
}

export interface UIState {
    route: string;
    code: string;
    vat: boolean;
    country: CountryData;
    showCountryChangeInfo: boolean;
    activeProduct: number;
    activeLogoArea: number;
    previewArticle1: string;
    previewArticle2: string;
    detailArticleIndex: number;
    selectedTag: string;
    mobileOrderStep: string;
    editScreenTab: string;
    isLoadingConfig: boolean;
    loadingConfigError: string | null;
    isStoringConfig: boolean;
    storingConfigError: string | null;
    isSendingConfig: boolean;
    sendingConfigError: string | null;
    isStoringLogo: boolean;
    storingLogoError: string | null;
    customBaseColor: string | null;
    customContrastColor: string | null;
}

export interface PricingData {
    eur: number;
    chf: number;
}

export interface PricingStep {
    pricing: PricingData;
    minAmount: number;
}

export enum DeliveryStatus {
    Available = "available",
    Wait = "wait",
    NotAvailable = "notavailable"   
}

export interface DeliveryData {
    deliveryTime: Translation;
    status: DeliveryStatus;
}

export interface LogoPlaneSize {
    width: number;
    height: number;
}

export interface LogoPlaneData {
    id: number;
    name: Translation;
    transferSize?: LogoPlaneSize;
    stitchSize?: LogoPlaneSize;
}


export interface LogoArea {
    id: string;
    type: LogoType;
    name: Translation;
    minSize: number;
    maxSize: number;
    pricing: PricingStep[];
}

export interface ModelData {
    cameraStartPosition?: number[];
    cameraStartPositionRight?: number[];
    cameraTargetPosition?: number[];
    cameraTargetPositionRight?: number[];
    minPolarAngle?: number;
    maxPolarAngle?: number;
    minDistance?: number;
    maxDistance?: number;
}

export interface ProductData {
    article: string;
    name: Translation;
    pricing: PricingStep[];
    productImage: string;
    articleDescriptionUrl: Translation;
    deliveryTime: Translation; 
    sizes: string[];
    tags: string[];
    hasVentilationZipper: boolean;
    ventilationArticle?: string;
    nonVentilationArticle?: string;
    modelData?: ModelData;
    modelDataThumbnail?: ModelData;
    logoPlanes: LogoPlaneData[];
}

export interface ShippingData {
    minPricing: PricingData;
    costs: PricingData;
}

export interface ColorData {
    name: Translation;
    id: string;
    color: string;
    bgColor: string;
    modelColor?: string;
    searchString?: Translation;
}

export enum StraussLogoType {
    Standard = "standard",
    StitchedBlack = "stitched_black",
    StitchedWhite = "stitched_white"
}

export enum Gender {
    Undefined = "undefined",
    Female = "female",
    Male = "male"
}

export interface InspirationData {
    article1: string;
    article2: string;
    baseColorId: string;
    contrastColorId: string;
    straussLogo: StraussLogoType;
    gender: Gender;
    centerImage1: string;
    centerImage2: string;
}

export interface MinimumOrderAmountEntry {
    articles: string[];
    amount: number;
}

export interface MinimumOrderAmount {
    defaultAmount: number;
    products: MinimumOrderAmountEntry[];
}

export interface LibraryData {
    baseColors: ColorData[];
    contrastColors: ColorData[];
    inspirations: InspirationData[];
    logoAreas: LogoArea[];
    products: ProductData[];
    minimumOrderAmount: MinimumOrderAmount;
}

export enum LogoType {
    Transfer = "transfer",
    Stitched = "stitched"   
}

export interface LogoState {
    filename: string;
    position: number;
    logoAreaId: string;    
    logoType: LogoType;
}

export interface LogoFile {
    originalFilename: string;
    filename: string;
    size: number;
}

export interface ProductOrderState {
    size: string;
    amount: number;
}

export interface ProductState {
    article: string;
    order: ProductOrderState[];
    logos: LogoState[];
}

export interface ConfigState {
    code: string | null;
    baseColorId: string;
    contrastColorId: string;
    straussLogo: StraussLogoType;
    logoFiles: LogoFile[];
    products: ProductState[];
    addBasics: boolean;
    inspectColor: boolean;
    inspectSize: boolean;
}

export interface ContactState {
    orderFor: string;
    customerNumber: string;
    firstName: string;
    lastName: string;
    country: string;
    zipCode: string;
    city: string;
    address: string;
    title: string;
    occupation: string;
    email: string;
    phone: string;
    comment: string;
}

export interface ApplicationState {
    ui: UIState;
    library: LibraryData;
    config: ConfigState;
    contact: ContactState;
}